import { Period } from '@spike/model';
import clsx from 'clsx';
import { SelectionBar } from 'components/UI';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, Ref, useState } from 'react';
import { formatSalesReportItemValue, salesReportItemvalueType } from 'utils/PaymentsUtils';
import Title from '../../ui/Title';
import { categories, getCategoryStatuses, All } from './InvoicesStatuses';
import InvoicesFilters from './InvoicesFilters';
import styles from './InvoicesHeader.module.scss';
import SummaryCard from 'components/UI/SummaryCard';

interface InvoicesHeaderProps {
    period: Period;
    clientId: number | null;
    hideClientFilter?: boolean;
    optionSelected: string | undefined;
    totalElements?: number;
    initialMarketplaceIds?: number[];
    summary: {
        totalOutstandingAmount: number;
        invoicesCount: number;
        clientsWithDebtCount: number;
        loading: boolean;
    };
    onChangeStatuses: (stauses: Array<string>) => void;
    onChangePeriod: (period: Period, optionSelected: string | undefined) => void;
    onChangeClient: (clientId: number | null) => void;
    onChangeMarketplaceIds: (marketplaceIds: Array<number>) => void;
    exporting: boolean;
    exportRef: Ref<{ export: () => void }>;
    exportRecords: Array<Array<string>>;
    exportFilename: string;
    onExport: () => void;
}

export const InvoicesHeader: FunctionComponent<InvoicesHeaderProps> = props => {
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const [selectedCategoryId, setSelectedCategoryId] = useState<string>(All.id);

    const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<Array<number>>(
        props.initialMarketplaceIds ?? organizationMarketplaces.map(m => m.id)
    );

    const changeCategoryHandler = (nextCategoryId: string) => {
        setSelectedCategoryId(nextCategoryId);
        props.onChangeStatuses(getCategoryStatuses(nextCategoryId));
    };

    const changeMarketplaceIdsHandler = (marketplaceIds: Array<number>) => {
        setSelectedMarketplaceIds(marketplaceIds);
        props.onChangeMarketplaceIds(marketplaceIds);
    };

    return (
        <>
            <div className={clsx(styles.container, styles.titleContainer)}>
                <div className={styles.left}>
                    <Title text="Invoices" />
                </div>
            </div>
            <div className={clsx(styles.container, styles.summaryContainer)}>
                <SummaryCard
                    title="Total outstanding"
                    value={formatSalesReportItemValue(
                        props.summary.totalOutstandingAmount,
                        salesReportItemvalueType.Money
                    )}
                    loading={props.summary.loading}
                    color="white"
                    className={styles.summaryCard}
                />
                <SummaryCard
                    title="Count of Invoices"
                    value={props.summary.invoicesCount}
                    loading={props.summary.loading}
                    color="white"
                    className={styles.summaryCard}
                />
                <SummaryCard
                    title="Count of clients with debt"
                    value={props.summary.clientsWithDebtCount}
                    loading={props.summary.loading}
                    color="white"
                    className={styles.summaryCard}
                />
            </div>
            <div className={clsx(styles.container, styles.filterContainer)}>
                <SelectionBar
                    options={[...categories]}
                    selectedId={selectedCategoryId}
                    onSelect={changeCategoryHandler}
                    selectedTotalRecords={props.summary.loading ? undefined : props.totalElements}
                    className={styles.selectionBar}
                />
                <InvoicesFilters
                    selectedMarketplaceIds={selectedMarketplaceIds}
                    clientId={props.clientId}
                    hideClientFilter={props.hideClientFilter}
                    period={props.period}
                    optionSelected={props.optionSelected}
                    onChangeClient={props.onChangeClient}
                    onChangePeriod={props.onChangePeriod}
                    onChangeMarketplaceIds={changeMarketplaceIdsHandler}
                    exporting={props.exporting}
                    exportRef={props.exportRef}
                    exportRecords={props.exportRecords}
                    exportFilename={props.exportFilename}
                    onExport={props.onExport}
                />
            </div>
        </>
    );
};

export default InvoicesHeader;
